<script setup>
import { computed, ref, watch } from 'vue'
import { VOIVODESHIPS, completePostalCode, formatPostalCode, lookupPostalCode, parseAddress, polish } from '@/lib/address.js'

/**
 * A structured address (D-89): street, building, postal code, city, region, country (ISO-2).
 * Quick to fill (D-197): a Polish postal code fills an empty town and voivodeship (or offers them
 * when they say otherwise), a voivodeship is picked from the list, and an address pasted whole
 * into the street goes to its fields.
 */
const props = defineProps({ modelValue: { type: Object, default: null }, disabled: Boolean })
const emit = defineEmits(['update:modelValue'])
const listId = `voivodeships-${Math.random().toString(36).slice(2, 8)}`
// What the postal code filled in, so another code replaces it rather than what was typed.
const filled = ref({})
const suggestion = ref(null) // {code, city, region} when it differs from what is there

function write(next) {
  const empty = Object.values(next).every((v) => !v)
  emit('update:modelValue', empty ? null : next)
}
function set(part, value) {
  if (part === 'city' || part === 'region') filled.value = { ...filled.value, [part]: undefined }
  write({ ...(props.modelValue || {}), [part]: value || null })
}
const v = (part) => props.modelValue?.[part] || ''
const isPolish = computed(() => polish(props.modelValue))

function setCode(value) {
  set('postal_code', isPolish.value ? formatPostalCode(value) : value)
}

// Each source on its own: the code changing, or the country — not every write of the address.
watch([() => props.modelValue?.postal_code, isPolish], async ([code, pl]) => {
  suggestion.value = null
  if (!pl || !completePostalCode(code) || props.disabled) return
  const found = await lookupPostalCode(code.trim())
  if (props.modelValue?.postal_code !== code) return
  const now = props.modelValue || {}
  const next = { ...now }
  const took = {}
  for (const part of ['city', 'region']) {
    const value = found?.[part] || null
    // What an earlier code put there and nobody changed belongs to that code, not this one.
    if (!value && filled.value[part] && filled.value[part] === now[part]) {
      next[part] = null
      took[part] = undefined
      continue
    }
    if (!value) continue
    // Empty, or what an earlier code put there: this code's.
    if (now[part] !== value && (!now[part] || (filled.value[part] && filled.value[part] === now[part]))) {
      next[part] = value
      took[part] = value
    }
  }
  if (Object.keys(took).length) {
    filled.value = { ...filled.value, ...took }
    write(next)
  }
  const differs = !!found && ['city', 'region'].some((p) => found[p] && next[p] && next[p].toLowerCase() !== found[p].toLowerCase())
  if (differs) suggestion.value = { code, city: found.city, region: found.region }
})

function useSuggestion() {
  const s = suggestion.value
  if (!s) return
  const next = { ...(props.modelValue || {}), ...(s.city ? { city: s.city } : {}), ...(s.region ? { region: s.region } : {}) }
  filled.value = { city: s.city, region: s.region }
  suggestion.value = null
  write(next)
}

/** An address pasted whole into the street goes to its fields. */
function pasteStreet(event) {
  const parsed = parseAddress(event.clipboardData?.getData('text') || '')
  if (!parsed) return
  event.preventDefault()
  const next = { ...(props.modelValue || {}) }
  for (const [part, value] of Object.entries(parsed)) {
    if (!value) continue
    next[part] = value
    filled.value = { ...filled.value, [part]: undefined }
  }
  write(next)
}
</script>

<template>
  <div class="grid grid-cols-6 gap-2">
    <input class="input col-span-6" :value="v('name')" :placeholder="$t('address.name')" :disabled="disabled" data-testid="address-name" @input="set('name', $event.target.value)" />
    <input class="input col-span-4" :value="v('street')" :placeholder="$t('address.street')" :disabled="disabled" :title="$t('address.pasteHint')" data-testid="address-street" @input="set('street', $event.target.value)" @paste="pasteStreet" />
    <input class="input col-span-2" :value="v('building')" :placeholder="$t('address.building')" :disabled="disabled" data-testid="address-building" @input="set('building', $event.target.value)" />
    <input class="input col-span-2" :value="v('postal_code')" placeholder="00-000" :disabled="disabled" inputmode="numeric" data-testid="address-postal-code" @input="setCode($event.target.value)" />
    <input class="input col-span-4" :value="v('city')" :placeholder="$t('address.city')" :disabled="disabled" data-testid="address-city" @input="set('city', $event.target.value)" />
    <input class="input col-span-4" :value="v('region')" :placeholder="$t('address.region')" :disabled="disabled" :list="isPolish ? listId : undefined" data-testid="address-region" @input="set('region', $event.target.value)" />
    <input class="input col-span-2 uppercase" maxlength="2" :value="v('country')" placeholder="PL" :disabled="disabled" data-testid="address-country" @input="set('country', $event.target.value.toUpperCase())" />
    <datalist v-if="isPolish" :id="listId"><option v-for="r in VOIVODESHIPS" :key="r" :value="r" /></datalist>
    <p v-if="suggestion" class="col-span-6 flex flex-wrap items-center gap-2 text-xs text-muted" data-testid="address-suggestion">
      <i class="fa-solid fa-location-dot" aria-hidden="true"></i>
      {{ $t('address.codeSays', { code: suggestion.code, place: [suggestion.city, suggestion.region].filter(Boolean).join(', ') }) }}
      <button type="button" class="font-medium text-brand-700 hover:underline dark:text-brand-300" @click="useSuggestion">{{ $t('address.useIt') }}</button>
    </p>
  </div>
</template>
